/**
 * Fantasy Console 0 - Edit Cart Modal
 *
 * Modal for editing cart title and description.
 */

import { useState, useCallback, useEffect, useRef } from 'react'

interface EditCartModalProps {
  isOpen: boolean
  cartId: string
  currentTitle: string
  currentDescription: string
  onSave: (cartId: string, title: string, description: string) => Promise<void>
  onClose: () => void
}

export function EditCartModal({
  isOpen,
  cartId,
  currentTitle,
  currentDescription,
  onSave,
  onClose,
}: EditCartModalProps) {
  const [title, setTitle] = useState(currentTitle)
  const [description, setDescription] = useState(currentDescription)
  const [isSaving, setIsSaving] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setTitle(currentTitle)
      setDescription(currentDescription)
      setTimeout(() => titleInputRef.current?.focus(), 100)
    }
  }, [isOpen, currentTitle, currentDescription])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const trimmedTitle = title.trim()
      if (!trimmedTitle) return

      setIsSaving(true)
      try {
        await onSave(cartId, trimmedTitle, description.trim())
        onClose()
      } finally {
        setIsSaving(false)
      }
    },
    [cartId, title, description, onSave, onClose],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  if (!isOpen) return null

  return (
    <div className='modal-overlay' onClick={onClose} onKeyDown={handleKeyDown}>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div className='modal-header'>
          <h2 className='modal-title'>Edit Cart Details</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className='modal-body'>
            <div className='form-group'>
              <label className='form-label' htmlFor='edit-cart-title'>
                Title
              </label>
              <input
                ref={titleInputRef}
                id='edit-cart-title'
                type='text'
                className='form-input'
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder='Cart title'
                maxLength={100}
                required
              />
            </div>

            <div className='form-group'>
              <label className='form-label' htmlFor='edit-cart-description'>
                Description
              </label>
              <textarea
                id='edit-cart-description'
                className='form-textarea'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder='A short description of your cart...'
                maxLength={500}
                rows={4}
              />
            </div>
          </div>

          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-ghost'
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={isSaving || !title.trim()}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
